import React, {useEffect, useState} from 'react';
import {BootstrapColors} from '../../../@types/Bootstrap';
import classnames from '../../../helpers/classnames';
import {animate} from '../../../helpers/animate/animate';

export default function CircularProgressBar(props: {
    size: number,
    progress?: number,
    shouldSpin?: boolean,
    color: BootstrapColors,
    transparentTrack?: boolean,
    trackWidth?: number,
    trackColor?: React.CSSProperties['color'],
    indicatorWidth?: number,
    indicatorColor?: React.CSSProperties['color'],
    indicatorCap?: 'butt' | 'round' | 'square' | 'inherit',
    children?: React.ReactNode,
    className?: string,
    style?: Omit<React.CSSProperties, 'width' | 'height'>,
    containerSizesModifiers?: [number, number],
    svgSizesModifiers?: [number, number],
}) {
    const {
        size,
        progress = 0,
        shouldSpin = true,
        color = 'primary',
        trackWidth = props.indicatorWidth ?? 2,
        indicatorWidth = props.trackWidth ?? 2,
        indicatorCap = 'round',
        containerSizesModifiers = [0, 0],
        svgSizesModifiers = [0, 0],
    } = props;

    const isLoader = props.progress == undefined;
    const [internalProgress, setInternalProgress] = useState(progress);

    useEffect(() => {
        if (!isLoader) {
            return;
        }

        const stop = animate({
            duration: 2000,
            draw: progress => setInternalProgress(progress * 200),
            infinity: true,
        });

        return () => stop();
    }, [isLoader]);

    const center = size / 2;
    const radius = Math.abs(center - (trackWidth > indicatorWidth ? trackWidth : indicatorWidth));
    const dashArray = 2 * Math.PI * radius;
    const dashOffset = dashArray * ((100 - (isLoader ? internalProgress : progress)) / 100);

    return <div
            className={classnames(props.className, 'circular-progress-bar', `circular-progress-bar--${color}`, {
                'circular-progress-bar--transparent-track': props.transparentTrack,
                'circular-progress-bar--should-spin': shouldSpin,
            })}
            style={{
                ...(props.style ?? {}),
                width: size + containerSizesModifiers[1],
                height: size + containerSizesModifiers[0],
                '--circular-progress-bar--track-color': props.trackColor,
                '--circular-progress-bar--indicator-color': props.indicatorColor,
            } as React.CSSProperties}
    >
        <svg className="circular-progress-bar__svg" style={{
            width: size + svgSizesModifiers[1],
            height: size + svgSizesModifiers[0],
        }}>
            <circle
                    className="circular-progress-bar__svg__track"
                    cx={center}
                    cy={center}
                    fill="transparent"
                    r={radius}
                    strokeWidth={trackWidth}
            />
            <circle
                    className="circular-progress-bar__svg__indicator"
                    cx={center}
                    cy={center}
                    fill="transparent"
                    r={radius}
                    strokeWidth={indicatorWidth}
                    strokeDasharray={dashArray}
                    strokeDashoffset={dashOffset}
                    strokeLinecap={indicatorCap}
            />
        </svg>

        <div className="circular-progress-bar__content">{props.children}</div>
    </div>;
}
